import React, { createContext, useContext, ReactNode, useMemo, useState } from 'react';
import { BranchCode } from 'api/risk';
import { TabConfig } from 'components/navigation/Tabs/useConfigTabs';
import { getBranchTabConfig } from './contractInfosTabsConfigs';

// Interface pour l'état partagé du contexte
export interface ContractInfosContextState {
  branchCode: BranchCode;
  tabs: TabConfig[];
  contractId: string
}

// Type complet du contexte
export type ContractInfosContextType = ContractInfosContextState & {
  // Risque parc : renseigné par l'onglet bien une fois le risque chargé,
  // pour renommer l'onglet « Informations du bien » en « Parc immobilier ».
  setIsParc: (isParc: boolean) => void;
};

// Création du contexte
const ContractInfosContext = createContext<ContractInfosContextType | undefined>(undefined);

// Props du provider
interface ContractInfosProviderProps {
  children: ReactNode;
  branchCode: BranchCode;
  contractId: string;
}

// Provider du contexte
export const ContractInfosProvider: React.FC<ContractInfosProviderProps> = ({
  children,
  branchCode,
  contractId
}) => {
  const [isParc, setIsParc] = useState(false);

  // Calcul des tabs basé sur le branchCode (mémorisé pour éviter les recalculs)
  const tabs = useMemo(() => {
    const config = getBranchTabConfig(branchCode);
    if (!isParc) return config;
    return config.map(tab =>
      tab.label === 'CONTRACT.TABS.CONTRACT_INFOS.REAL_ESTATE_INFOS'
        ? { ...tab, label: 'CONTRACT.TABS.CONTRACT_INFOS.PARC_INFOS' }
        : tab
    );
  }, [branchCode, isParc]);

  // Valeur du contexte
  const contextValue: ContractInfosContextType = {
    branchCode,
    tabs,
    contractId,
    setIsParc
  };

  return (
    <ContractInfosContext.Provider value={contextValue}>
      {children}
    </ContractInfosContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte complet
export const useContractInfos = (): ContractInfosContextType => {
  const context = useContext(ContractInfosContext);
  if (context === undefined) {
    throw new Error('useContractInfos must be used within a ContractInfosProvider');
  }
  return context;
};

// Hook pour accéder seulement aux données (lecture seule)
export const useContractInfosState = (): ContractInfosContextState => {
  const { branchCode, tabs, contractId } = useContractInfos();
  return {
    branchCode,
    tabs,
    contractId
  };
};
